<template>
    <div v-if="!currentPageItems.length" class="master-template-select__empty-wrapper">
        <hox-empty-message>
            <template #title>
                <template v-if="hasCampaignItems">There aren't any new templates in your library</template>
                <template v-else>There are no templates in your library</template>
            </template>
            <template #actionItems>
                <MasterTemplateUploadButton
                    v-if="$auth.userCan($auth.Actions.CanManageMasterTemplates, { clientId }, false)"
                    label="Upload a template"
                    data-testid="upload-template__button"
                />
            </template>
        </hox-empty-message>
    </div>

    <template v-else>
        <div v-if="viewMode === ViewMode.List" class="master-template-select__table" :class="$attrs.class">
            <MasterTemplateTable
                v-model="selectedItems"
                :items="currentPageItems"
                :is-selectable="hasCampaignItems"
                @remove="onRemove"
            />
        </div>

        <CheckboxGroup
            v-else
            ref="body"
            v-model="selectedItems"
            class="master-template-select__grid"
            :class="$attrs.class"
        >
            <template v-if="!masterTemplatesLibraryStore.families.length">
                <MasterTemplatePreview
                    v-for="masterTemplateId in currentPageItems"
                    :key="masterTemplateId"
                    class="master-template-select__grid-item"
                    :is-selectable="isSelectable(masterTemplateId)"
                    :max-height="bodyDimensionsPixels.height"
                    :max-width="bodyDimensionsPixels.width"
                    :scaling-factor="scalingFactor"
                    :master-template="masterTemplates[masterTemplateId]"
                    @remove="onRemove(masterTemplateId)"
                    @resizeToggle="onResize(masterTemplateId, $event)"
                />
            </template>

            <template v-else-if="masterTemplatesLibraryStore.families.length > 0">
                <div v-for="{ parentId, children } in gridItemFamilies" :key="parentId" class="template-family">
                    <div v-if="parentId" class="template-family__parent">
                        <h4>Parent Template</h4>
                        <div
                            class="campaign-edit__deliverable"
                            :class="{
                                'campaign-edit__deliverable--resizable': resizedBanners.includes(parentId)
                            }"
                        >
                            <MasterTemplatePreview
                                class="master-template-select__grid-item"
                                :is-selectable="isSelectable(parentId)"
                                :max-height="bodyDimensionsPixels.height"
                                :max-width="bodyDimensionsPixels.width"
                                :scaling-factor="scalingFactor"
                                :master-template="masterTemplates[parentId]"
                                @remove="onRemove(parentId)"
                                @resizeToggle="onResize(parentId, $event)"
                            />
                        </div>
                    </div>

                    <div v-if="children.length" class="template-family__children">
                        <h4>Resized Templates</h4>
                        <div
                            v-for="masterTemplateId in children"
                            :key="masterTemplateId"
                            class="campaign-edit__deliverable"
                            :class="{
                                'campaign-edit__deliverable--resizable': resizedBanners.includes(masterTemplateId)
                            }"
                        >
                            <MasterTemplatePreview
                                :is-selectable="isSelectable(masterTemplateId)"
                                :max-height="bodyDimensionsPixels.height"
                                :max-width="bodyDimensionsPixels.width"
                                :scaling-factor="scalingFactor"
                                :master-template="masterTemplates[masterTemplateId]"
                                @remove="onRemove(masterTemplateId)"
                                @resizeToggle="onResize(masterTemplateId, $event)"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </CheckboxGroup>

        <div class="master-template-select__footer">
            <div class="master-templates-select__paging paging-controls">
                <Page
                    v-if="sortedItems.length"
                    :model-value="page"
                    :total="sortedItems.length"
                    :page-size="pageSize"
                    :page-size-opts="pageSizeOpts"
                    @onChange="setPage"
                />

                <Select v-model="pageSize" class="hidden">
                    <Option v-for="sizeOpt in pageSizeOpts" :key="sizeOpt.value" :value="sizeOpt.value">
                        {{ sizeOpt.label }}
                    </Option>
                </Select>
            </div>
        </div>
    </template>
</template>

<script lang="ts">
import { defineComponent, ref, type PropType } from "vue";
import { mapStores } from "pinia";
import { TemplateType } from "hoxton-shared/enums/masterTemplate";

import { TemplatesSortBy } from "@/enums/masterTemplate";
import MasterTemplates from "@/services/MasterTemplates";
import MasterTemplatePreview from "@/components/Campaign/MasterTemplatePreview.vue";
import MasterTemplateUploadButton from "@/components/Campaign/MasterTemplates/MasterTemplateUploadButton.vue";
import HoxEmptyMessage from "@/components/common/EmptyMessage.vue";
import MasterTemplateTable from "@/components/Campaign/MasterTemplates/MasterTemplatesTable.vue";

import { UiAction } from "@/store/modules/ui";
import { MultiviewEvent, MultiviewMode } from "@/enums/multiview.ts";
import { usePagination } from "@/composables/usePagination";
import bus from "@/bus";
import { useScalingBannerWrapper } from "@/composables/useScalingBannerWrapper";
import { useMasterTemplatesLibraryStore } from "@/store/pinia/masterTemplatesLibrary";

export default defineComponent({
    name: "MasterTemplateSelect",

    components: {
        MasterTemplateTable,
        MasterTemplateUploadButton,
        MasterTemplatePreview,
        HoxEmptyMessage
    },

    props: {
        campaignTemplateIds: {
            type: [Array, null] as PropType<string[] | null>,
            default() {
                return null;
            }
        },

        instanceName: {
            type: String,
            default: "multiview"
        },

        items: {
            type: Array as PropType<string[]>,
            required: true
        },

        sortBy: {
            type: Object as PropType<(typeof TemplatesSortBy)[keyof typeof TemplatesSortBy]>,
            default() {
                return TemplatesSortBy.WIDTH_ASC;
            }
        }
    },

    emits: ["select"],

    setup() {
        const body = ref();
        const { page, pageSize, setPage, currentPageItems, setPaginationData } = usePagination<string>();
        const { bodyDimensionsPixels, scalingFactor, setBodyDimensionsPixels } = useScalingBannerWrapper({
            bodyRef: body
        });

        return {
            page,
            setPage,
            pageSize,
            currentPageItems,
            setPaginationData,
            bodyDimensionsPixels,
            scalingFactor,
            body,
            setBodyDimensionsPixels
        };
    },

    data() {
        return {
            resizedBanners: [] as string[],
            selected: [] as string[],
            masterTemplateService: null as MasterTemplates | null,
            ViewMode: MultiviewMode,
            defaultPageSize: 10
        };
    },

    computed: {
        ...mapStores(useMasterTemplatesLibraryStore),
        clientId() {
            return this.$route.params.clientId as string;
        },

        filteredItems() {
            if (this.campaignTemplateIds?.length) {
                return this.items.filter(item => !this.campaignTemplateIds?.includes(item));
            }

            return this.items;
        },

        gridItemFamilies() {
            const faimiliesByParentId = this.currentPageItems.reduce<
                Record<string, { parentId: string; children: string[] }>
            >((acc, templateId) => {
                const template = this.masterTemplates[templateId];

                if (!template) {
                    return acc;
                }

                const id = template.parentId || template._id;

                acc[id] = acc[id] ?? { parentId: null, children: [] };

                if (!template.parentId) {
                    acc[template._id].parentId = template._id;
                } else {
                    acc[template.parentId].children.push(template._id);
                }

                return acc;
            }, {});

            return Object.values(faimiliesByParentId);
        },

        hasCampaignItems() {
            return this.campaignTemplateIds !== null;
        },

        masterTemplates() {
            return this.masterTemplatesLibraryStore.masterTemplates;
        },

        pageSizeOpts() {
            return this.pageSizes.map(size => ({
                value: size,
                label: `${size}/page`
            }));
        },

        pageSizes() {
            return [this.defaultPageSize];
        },

        selectedItems: {
            get() {
                return this.selected;
            },

            set(value: string[]) {
                this.selected = value;
                this.$emit("select", value);

                this.$store.dispatch(UiAction.MultiviewUpdate, {
                    instance: this.instanceName,
                    selected: this.selected.length,
                    pageCount: this.currentPageItems.length,
                    totalCount: this.sortedItems.length
                });
            }
        },

        sortedItems() {
            return this.filteredItems.toSorted((a, b) => {
                const masterTemplateA = this.masterTemplates[a];
                const masterTemplateB = this.masterTemplates[b];

                if (!masterTemplateA || !masterTemplateB) {
                    return 0;
                }

                const directionMultiplier = this.sortBy.dir === "asc" ? -1 : 1;

                if (masterTemplateA[this.sortBy.field] < masterTemplateB[this.sortBy.field]) {
                    return directionMultiplier;
                }

                if (masterTemplateA[this.sortBy.field] > masterTemplateB[this.sortBy.field]) {
                    return directionMultiplier * -1;
                }

                return 0;
            });
        },

        viewMode() {
            return this.masterTemplatesLibraryStore.viewMode;
        },

        filters() {
            return {
                sizes: this.masterTemplatesLibraryStore.sizes,
                types: this.masterTemplatesLibraryStore.types,
                families: this.masterTemplatesLibraryStore.families,
                search: this.masterTemplatesLibraryStore.search,
                hideResized: this.masterTemplatesLibraryStore.hideResized
            };
        }
    },

    watch: {
        items: {
            handler() {
                this.selectedItems = [];
            },
            deep: true
        },

        sortedItems: {
            handler(newVal?: string[]) {
                this.$nextTick(() => {
                    this.setPaginationData(newVal ?? []);
                    this.setPage(1);
                });
            },
            immediate: true
        },

        viewMode(mode) {
            if (mode === MultiviewMode.Grid) {
                this.setBodyDimensionsPixels();
            }
        },

        currentPageItems() {
            // reset selection on page change
            this.selectedItems = [];
        },

        filters() {
            this.setPage(1);
        }
    },

    created() {
        // @ts-expect-error known issue
        this.masterTemplateService = new MasterTemplates(this.$apollo, this.clientId);
        this.initMultiviewPagination();
    },

    mounted() {
        this.selectedItems = []; // forcing setting initial vuex state
    },

    methods: {
        isSelectable(templateId: string) {
            // preview is selectable if we have campaignTemplateIds and
            // if it's an AE template, if the template is complete
            if (this.campaignTemplateIds === null) {
                return false;
            }

            if (this.masterTemplates[templateId]?.adType === TemplateType.AE) {
                return this.masterTemplates[templateId].aeInfo.completed;
            }

            return true;
        },

        async onRemove(masterTemplateId: string) {
            try {
                await this.masterTemplateService?.delete(masterTemplateId);
                this.$snackbar.success("Success", "The master template was successfully deleted!");
            } catch (e) {
                this.$snackbar.error("Deletion Failed", (e as Error).message, {
                    duration: 0,
                    closable: true
                });
            }
        },

        onResize(masterTemplateId: string, isResized: boolean) {
            if (isResized) {
                this.resizedBanners.push(masterTemplateId);
            } else {
                this.resizedBanners = this.resizedBanners.filter(id => masterTemplateId !== id);
            }
        },

        initMultiviewPagination() {
            bus.$on(MultiviewEvent.SelectAllOnPage, this.selectAllOnPage.bind(this));
            bus.$on(MultiviewEvent.SelectAll, this.selectAll.bind(this));
        },

        selectAll(data: { instance: string }) {
            if (data.instance !== this.instanceName) {
                return;
            }

            this.selectedItems = this.sortedItems;
        },

        selectAllOnPage(data: { value: boolean; instance: string }) {
            if (data.instance !== this.instanceName) {
                return;
            }

            this.selectedItems = data.value ? this.currentPageItems : [];
        }
    }
});
</script>
<style lang="scss">
@use "@style/variables" as *;

.master-template-select__empty-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
}

.master-template-select__content {
    height: 100%;
}

.master-template-select__table,
.master-template-select__grid {
    padding: $spacing-large 20px $spacing;
}

.master-template-select__table {
    overflow-y: auto;
}

.master-template-select__grid {
    overflow: auto;

    display: flex;
    flex-wrap: wrap;
    gap: 15px;
}

.master-template-select__grid-item {
    vertical-align: top;
    flex-grow: 0;
    flex-shrink: 0;
}

.master-template-select__footer {
    padding: $spacing 20px;
    border-top: 1px solid $grey3;
}
</style>
